// v2.9.8 · Flow Monitor
// Trazabilidad end-to-end de una OT en 5 pasos: Odoo → Filtro → A1 → A2 → Kanban.
import { useState } from "react";
import Layout from "../components/Layout";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Search, Route, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STEP_ICONS = {
  1: "🔌",
  2: "🚪",
  3: "🧠",
  4: "📅",
  5: "🗂",
};

function StepCard({ step }) {
  const skipped = !!step.skipped;
  const ok = !!step.ok;
  const color = skipped
    ? "border-gray-300 bg-gray-50"
    : ok
    ? "border-emerald-300 bg-emerald-50"
    : "border-red-300 bg-red-50";
  const IconEl = skipped ? AlertTriangle : ok ? CheckCircle2 : XCircle;
  const iconClr = skipped ? "text-gray-500" : ok ? "text-emerald-600" : "text-red-600";

  return (
    <div className={`border-2 ${color} rounded-xl p-4 space-y-2`} data-testid={`flow-step-${step.step}`}>
      <div className="flex items-center gap-2">
        <div className="text-2xl">{STEP_ICONS[step.step]}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <IconEl size={16} className={iconClr} />
            <h3 className="text-sm font-semibold text-slate-800">
              Paso {step.step} · {step.name}
            </h3>
          </div>
          <p className="text-xs text-slate-700 mt-0.5">{step.reason || "—"}</p>
        </div>
      </div>
      {step.details && Object.keys(step.details).length > 0 && (
        <details className="text-[11px] bg-white/60 rounded p-2 border border-slate-200">
          <summary className="cursor-pointer font-mono text-slate-600 hover:text-slate-900">
            Ver detalles ({Object.keys(step.details).length})
          </summary>
          <pre className="mt-2 font-mono text-[10px] overflow-auto max-h-48">
            {JSON.stringify(step.details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

export default function FlowMonitor() {
  const [taskId, setTaskId] = useState("00321773");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runTrace = async () => {
    const ref = (taskId || "").trim();
    if (!ref || !/^\d+$/.test(ref.replace(/^0+/, "") || "0")) {
      toast.error("Ingresa un ID o task_number válido (ej. 321788 o 00321788)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      // No hacer strip de ceros: el backend acepta task_number con ceros
      const { data } = await api.get(`/admin/flow-monitor/${encodeURIComponent(ref)}`);
      setResult(data);
      if (data.stopped_at_step) {
        toast.warning(`Detenida en Paso ${data.stopped_at_step}`);
      } else {
        toast.success("OT completó todo el flujo");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error consultando trazabilidad");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Flow Monitor" subtitle="Trazabilidad end-to-end de una OT">
      <div className="max-w-4xl mx-auto space-y-4">
        <div
          className="rounded-xl p-4 space-y-3 border-2 border-cyan-200"
          style={{ background: "linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)" }}
          data-testid="flow-monitor-header"
        >
          <div className="flex items-center gap-2">
            <Route size={20} className="text-cyan-700" />
            <div>
              <h2 className="text-lg font-bold text-cyan-900">Trazabilidad de OT</h2>
              <p className="text-xs text-cyan-800">
                Ingresa un ID de Odoo para diagnosticar en qué paso del pipeline se detuvo la orden.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder="Ej: 00321773 o 998137"
              onKeyDown={(e) => { if (e.key === "Enter") runTrace(); }}
              className="flex-1 px-3 py-2 text-sm border border-cyan-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
              data-testid="flow-monitor-input"
            />
            <button
              onClick={runTrace}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:opacity-50"
              data-testid="flow-monitor-trace"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Trazar OT
            </button>
          </div>
        </div>

        {result && (
          <>
            {/* Resumen */}
            <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-center gap-3" data-testid="flow-summary">
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">
                  OT ref: <span className="font-mono">{result.ot_ref_input}</span>
                  {result.resolved_odoo_id ? (
                    <span className="ml-2 text-xs text-slate-600">
                      → id={result.resolved_odoo_id} · number={result.resolved_task_number}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Odoo: {result.exists_in_odoo ? "✅ existe" : "❌ no existe"}
                  <span className="mx-2">·</span>
                  master_ot: {result.exists_in_master_ot ? "✅ bajó" : "❌ no bajó"}
                </div>
              </div>
              {result.stopped_at_step ? (
                <div className="text-right">
                  <div className="text-xs text-red-700 font-medium">Detenida en</div>
                  <div className="text-2xl font-bold text-red-600">Paso {result.stopped_at_step}</div>
                </div>
              ) : (
                <div className="text-right">
                  <div className="text-xs text-emerald-700 font-medium">Estado</div>
                  <div className="text-lg font-bold text-emerald-600">Flujo completo</div>
                </div>
              )}
            </div>

            {/* Pasos */}
            <div className="space-y-2">
              {result.steps.map((step, idx) => (
                <div key={idx}>
                  <StepCard step={step} />
                  {idx < result.steps.length - 1 && (
                    <div className="flex justify-center py-1">
                      <ChevronRight size={16} className="text-slate-400 rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {!result && !loading && (
          <div className="text-center py-12 text-slate-400 text-sm">
            Ingresa un ID Odoo (ej. 00321773) y presiona &quot;Trazar OT&quot;.
          </div>
        )}
      </div>
    </Layout>
  );
}
